import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { useAuth } from "context/AuthContext";
import { useNotifications } from "hooks/useNotifications";
import { useUnreadMessageCount } from "services/firestore/getMessagesUnreadCountByUser";

import { appRoute } from "pages/app/app.routes";
import { instructorRoute } from "pages/instructor/instructor.routes";
import { defaultThemeColors, fonts, themeColor } from "ui/theme";
import { Dropdown } from "./Dropdown";

export const HeaderMenu = ({ primary, mobile }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    token,
    userData,
    userRole,
    toggleRole,
    setUserRole,
    logout,
    isLoading,
  } = useAuth();

  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showForgotPasswordModal, setForgotPasswordModal] = useState(false);

  const { data: notifications } = useNotifications(userRole, token);
  const currentUserId = userData?.id;
  const unreadMessagesCount = useUnreadMessageCount(currentUserId, userRole);
  const unreadNotificationCount = notifications?.[0]?.unread?.filter(
    (n) => n.status === "unread",
  ).length;

  const isInstructor = userRole === 2;
  const isLearner = userRole === 1;
  const profileComplete = +userData?.teacher?.profile_completion >= 75;
  const isInReview = userData?.teacher?.status === "awaiting";

  useEffect(() => {
    setUserRole(pathname.includes("instructor") ? 2 : 1);
  }, [pathname]);

  const handleSwitch = (to) => {
    toggleRole();
    navigate(to);
  };

  const actionButton = useMemo(() => {
    const buttons = {
      loading: <LoadingText>Loading...</LoadingText>,

      guest: (
        <PrimaryButton
          primary={primary}
          onClick={() => setShowSignInModal(true)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setShowSignInModal(true)}
        >
          Become an Instructor
        </PrimaryButton>
      ),

      learnerIncomplete: (
        <PrimaryButton
          primary={primary}
          onClick={() => navigate(appRoute.completeProfile.fullPath)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate(appRoute.completeProfile.fullPath)}
        >
          Become an Instructor
        </PrimaryButton>
      ),

      learnerComplete: (
        <SwitchButton
          primary={primary}
          disabled={isInReview}
          onClick={() => handleSwitch(instructorRoute.homeInstructor)}
        >
          {isInReview ? "Listing in review" : "Switch to Teach"}
        </SwitchButton>
      ),

      instructor: (
        <SwitchButton primary={primary} onClick={() => handleSwitch("/")}>
          Switch to Learn
        </SwitchButton>
      ),
    };

    if (isLoading) return buttons.loading;
    if (!token) return buttons.guest;
    if (isInstructor) return buttons.instructor;
    if (isLearner && profileComplete) return buttons.learnerComplete;
    return buttons.learnerIncomplete;
  }, [
    isLoading,
    token,
    isInstructor,
    isLearner,
    profileComplete,
    isInReview,
    primary,
  ]);

  return (
    <Menu mobile={mobile}>
      {mobile && pathname !== "/" ? null : actionButton}
      <Dropdown
        userRole={userRole}
        userData={userData}
        isLoggedIn={!!token}
        onLogout={() => {
          logout();
          navigate("/");
        }}
        primary={primary}
        unreadMessagesCount={unreadMessagesCount}
        unreadNotificationCount={unreadNotificationCount}
        modalState={[showSignupModal, setShowSignupModal]}
        signInModalState={[showSignInModal, setShowSignInModal]}
        forgotPaswordModalState={[
          showForgotPasswordModal,
          setForgotPasswordModal,
        ]}
      />
    </Menu>
  );
};

const Menu = styled.div`
  z-index: 1;
  display: ${({ mobile }) => (mobile ? "flex" : "none")};
  justify-content: center;
  align-items: center;


  @media (min-width: 1024px) {
    display: flex;
  }

  @media (max-width: 600px) {
    margin-left: 1rem;
  }
  @media (max-width: 400px) {
    margin-left: 8px;
  }
`;

const PrimaryButton = styled.div.attrs(() => ({
  role: "button",
  tabIndex: 0,
}))`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: ${fonts.sizes.small};
  white-space: nowrap;
  height: 42px;
  padding: 10px 24px;
  font-weight: 600;
  border-radius: 100px; 

  ${({ primary, theme }) =>
    primary
      ? `
        background:${theme.colors.secondary};
        color: #1d1d1f;
        border: none;
        transition: background 0.2s ease, color 0.2s ease;
        &:hover { background: ${theme.colors.blue}; color: ${theme.colors.white}; }
        &:focus { outline: none; }
        &:focus-visible { outline: 2px solid ${theme.colors.blue}; outline-offset: 3px; background: ${theme.colors.blue}; color: ${theme.colors.white}; }
    `
      : `
        color: #1d1d1f;
        border: 2px solid #1d1d1f;
        background-color: white;
        transition: background 0.2s ease;
        &:hover { background: rgba(0, 0, 0, 0.05); }
        &:focus { outline: none; }
        &:focus-visible { outline: 2px solid #1d1d1f; outline-offset: 3px; background: rgba(0, 0, 0, 0.05); }
    `}

  @media (max-width: 400px) {
    
    font-weight: 400;
    ${({ primary }) => !primary && `width: auto;`}
    padding: 4px 10px;
  }
`;

const SwitchButton = styled.button`
  background: none;
  border: none;
  color: ${({ primary }) =>
    primary ? defaultThemeColors.white : defaultThemeColors.txt};
  font-size: ${fonts.sizes.regular};
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  &:hover {
    text-decoration: ${({ disabled }) => (disabled ? "none" : "underline")};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 3px;
    border-radius: 4px;
    text-decoration: underline;
  }
`;

const LoadingText = styled.p`
  color: ${themeColor("white")};
`;
